import posthog, {
  type CaptureOptions,
  type CaptureResult,
  type PostHogConfig,
} from "posthog-js";

export const LT_POSTHOG_EVENTS = {
  userAuthenticated: "lt_user_authenticated",
  workspaceActivated: "lt_workspace_activated",
  onboardingStarted: "lt_onboarding_started",
  onboardingCompleted: "lt_onboarding_completed",
  icpCreated: "lt_icp_created",
  firstVerifiedLead: "lt_first_verified_lead",
  leadQualified: "lt_lead_qualified",
  campaignCreated: "lt_campaign_created",
  campaignLaunched: "lt_campaign_launched",
  firstEmailSent: "lt_first_email_sent",
  positiveReply: "lt_positive_reply",
  meetingRequested: "lt_meeting_requested",
  meetingBooked: "lt_meeting_booked",
  meetingCompleted: "lt_meeting_completed",
  opportunityCreated: "lt_opportunity_created",
  actionProposed: "lt_action_proposed",
  actionExecuted: "lt_action_executed",
  actionBlocked: "lt_action_blocked",
} as const;

export type LaunchTactixPostHogEvent =
  (typeof LT_POSTHOG_EVENTS)[keyof typeof LT_POSTHOG_EVENTS];

type StableEventProperties = {
  /** A deterministic UUID for retry-safe PostHog deduplication. */
  eventId: string;
};

export type ProductSourceCategory =
  | "onboarding"
  | "prospecting"
  | "campaign"
  | "inbox"
  | "meeting"
  | "pipeline"
  | "automation";
export type ProductScoreBand = "low" | "medium" | "high";
export type ProductActionType =
  | "create"
  | "update"
  | "send"
  | "schedule"
  | "qualify"
  | "route";
export type ProductActionCategory =
  | "lead"
  | "campaign"
  | "email"
  | "meeting"
  | "opportunity";
export type ProductExecutionMode = "manual" | "assisted" | "autonomous";
export type ProductBlockerReasonCode =
  | "approval_required"
  | "policy"
  | "missing_data"
  | "integration_unavailable"
  | "rate_limited";

type ActionProperties = StableEventProperties & {
  actionType?: ProductActionType;
  actionCategory?: ProductActionCategory;
  executionMode?: ProductExecutionMode;
  scoreBand?: ProductScoreBand;
  sourceCategory?: ProductSourceCategory;
};

/**
 * The only caller-controlled properties accepted for product events. Workspace
 * identity is deliberately absent and is injected after fresh context exists.
 */
export interface ProductEventPropertiesMap {
  [LT_POSTHOG_EVENTS.onboardingStarted]: StableEventProperties;
  [LT_POSTHOG_EVENTS.onboardingCompleted]: StableEventProperties;
  [LT_POSTHOG_EVENTS.icpCreated]: StableEventProperties;
  [LT_POSTHOG_EVENTS.firstVerifiedLead]: StableEventProperties;
  [LT_POSTHOG_EVENTS.leadQualified]: StableEventProperties & {
    scoreBand?: ProductScoreBand;
    sourceCategory?: ProductSourceCategory;
  };
  [LT_POSTHOG_EVENTS.campaignCreated]: StableEventProperties & {
    sourceCategory?: ProductSourceCategory;
  };
  [LT_POSTHOG_EVENTS.campaignLaunched]: StableEventProperties & {
    executionMode?: ProductExecutionMode;
    sourceCategory?: ProductSourceCategory;
  };
  [LT_POSTHOG_EVENTS.firstEmailSent]: StableEventProperties & {
    executionMode?: ProductExecutionMode;
  };
  [LT_POSTHOG_EVENTS.positiveReply]: StableEventProperties & {
    sourceCategory?: ProductSourceCategory;
  };
  [LT_POSTHOG_EVENTS.meetingRequested]: StableEventProperties & {
    sourceCategory?: ProductSourceCategory;
  };
  [LT_POSTHOG_EVENTS.meetingBooked]: StableEventProperties & {
    sourceCategory?: ProductSourceCategory;
  };
  [LT_POSTHOG_EVENTS.meetingCompleted]: StableEventProperties & {
    sourceCategory?: ProductSourceCategory;
  };
  [LT_POSTHOG_EVENTS.opportunityCreated]: StableEventProperties & {
    sourceCategory?: ProductSourceCategory;
  };
  [LT_POSTHOG_EVENTS.actionProposed]: ActionProperties;
  [LT_POSTHOG_EVENTS.actionExecuted]: ActionProperties;
  [LT_POSTHOG_EVENTS.actionBlocked]: ActionProperties & {
    blockerReasonCode?: ProductBlockerReasonCode;
  };
}

export type ProductEventName = keyof ProductEventPropertiesMap;

export interface PostHogRuntimeConfig {
  enabled: boolean;
  key?: string;
  host?: string;
}

export interface PostHogClient {
  init(apiKey: string, config: Partial<PostHogConfig>): void;
  identify(distinctId: string): void;
  reset(): void;
  group(groupType: string, groupKey: string): void;
  resetGroups(): void;
  register(properties: Record<string, string>): void;
  unregister(property: string): void;
  capture(
    eventName: string,
    properties?: Record<string, string | number | boolean>,
    options?: Pick<CaptureOptions, "uuid">,
  ): CaptureResult | undefined;
}

const PRODUCT_PROPERTY_ENUMS = {
  actionType: new Set<ProductActionType>([
    "create",
    "update",
    "send",
    "schedule",
    "qualify",
    "route",
  ]),
  actionCategory: new Set<ProductActionCategory>([
    "lead",
    "campaign",
    "email",
    "meeting",
    "opportunity",
  ]),
  executionMode: new Set<ProductExecutionMode>([
    "manual",
    "assisted",
    "autonomous",
  ]),
  blockerReasonCode: new Set<ProductBlockerReasonCode>([
    "approval_required",
    "policy",
    "missing_data",
    "integration_unavailable",
    "rate_limited",
  ]),
  scoreBand: new Set<ProductScoreBand>(["low", "medium", "high"]),
  sourceCategory: new Set<ProductSourceCategory>([
    "onboarding",
    "prospecting",
    "campaign",
    "inbox",
    "meeting",
    "pipeline",
    "automation",
  ]),
} as const;

const ACTION_PROPERTY_KEYS = [
  "eventId",
  "actionType",
  "actionCategory",
  "executionMode",
  "scoreBand",
  "sourceCategory",
] as const;

const PRODUCT_PROPERTY_KEYS: Record<ProductEventName, ReadonlySet<string>> = {
  [LT_POSTHOG_EVENTS.onboardingStarted]: new Set(["eventId"]),
  [LT_POSTHOG_EVENTS.onboardingCompleted]: new Set(["eventId"]),
  [LT_POSTHOG_EVENTS.icpCreated]: new Set(["eventId"]),
  [LT_POSTHOG_EVENTS.firstVerifiedLead]: new Set(["eventId"]),
  [LT_POSTHOG_EVENTS.leadQualified]: new Set([
    "eventId",
    "scoreBand",
    "sourceCategory",
  ]),
  [LT_POSTHOG_EVENTS.campaignCreated]: new Set([
    "eventId",
    "sourceCategory",
  ]),
  [LT_POSTHOG_EVENTS.campaignLaunched]: new Set([
    "eventId",
    "executionMode",
    "sourceCategory",
  ]),
  [LT_POSTHOG_EVENTS.firstEmailSent]: new Set([
    "eventId",
    "executionMode",
  ]),
  [LT_POSTHOG_EVENTS.positiveReply]: new Set(["eventId", "sourceCategory"]),
  [LT_POSTHOG_EVENTS.meetingRequested]: new Set(["eventId", "sourceCategory"]),
  [LT_POSTHOG_EVENTS.meetingBooked]: new Set(["eventId", "sourceCategory"]),
  [LT_POSTHOG_EVENTS.meetingCompleted]: new Set(["eventId", "sourceCategory"]),
  [LT_POSTHOG_EVENTS.opportunityCreated]: new Set([
    "eventId",
    "sourceCategory",
  ]),
  [LT_POSTHOG_EVENTS.actionProposed]: new Set(ACTION_PROPERTY_KEYS),
  [LT_POSTHOG_EVENTS.actionExecuted]: new Set(ACTION_PROPERTY_KEYS),
  [LT_POSTHOG_EVENTS.actionBlocked]: new Set([
    ...ACTION_PROPERTY_KEYS,
    "blockerReasonCode",
  ]),
};

const PRODUCT_EVENT_NAMES = new Set<ProductEventName>(
  Object.keys(PRODUCT_PROPERTY_KEYS) as ProductEventName[],
);
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function validatedProductCapture(
  eventName: ProductEventName,
  input: unknown,
): {
  properties: Record<string, string | number | boolean>;
  uuid: string;
} | null {
  if (
    !PRODUCT_EVENT_NAMES.has(eventName) ||
    typeof input !== "object" ||
    input === null ||
    Array.isArray(input)
  ) {
    return null;
  }

  const raw = input as Record<string, unknown>;
  const allowedKeys = PRODUCT_PROPERTY_KEYS[eventName];
  if (
    Object.keys(raw).some((key) => !allowedKeys.has(key)) ||
    typeof raw.eventId !== "string" ||
    !UUID_PATTERN.test(raw.eventId)
  ) {
    return null;
  }

  const properties: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (key === "eventId") continue;
    if (!(key in PRODUCT_PROPERTY_ENUMS) || typeof value !== "string") {
      return null;
    }
    const allowedValues = PRODUCT_PROPERTY_ENUMS[
      key as keyof typeof PRODUCT_PROPERTY_ENUMS
    ] as ReadonlySet<string>;
    if (!allowedValues.has(value)) return null;
    properties[key] = value;
  }
  return { properties, uuid: raw.eventId };
}

const STATIC_ANALYTICS_PATHS = new Set([
  "/",
  "/sign-in",
  "/sign-up",
  "/profile",
  "/outreach",
  "/deals",
  "/social",
  "/seo",
  "/network",
  "/settings",
  "/inbox",
  "/listening",
  "/abtesting",
  "/reviews",
  "/intelligence/companies",
  "/intelligence/hot-accounts",
  "/intelligence/signals",
  "/intelligence/data-sources",
  "/intelligence",
  "/workspace-admin",
  "/admin",
  "/admin/health",
  "/admin/explorium",
  "/admin/jarvis",
  "/jarvis/pair",
  "/business-profiles",
  "/intelligence/deliverability",
  "/intelligence/data-quality",
  "/actions",
  "/settings/autonomous",
  "/settings/integrations/jarvis",
  "/reports",
  "/sms",
  "/scheduling",
  "/webhooks",
  "/deliverability",
  "/team",
  "/pricing",
  "/billing/success",
  "/billing/workspace-success",
  "/billing",
  "/compliance",
  "/compliance/audit-log",
  "/compliance/unsubscribes",
  "/compliance/bounce-log",
  "/compliance/suppression",
  "/compliance/soc2",
  "/compliance/iso27001",
  "/compliance/security-docs",
  "/compliance/settings",
  "/compliance/action-logs",
  "/compliance/asset-register",
  "/compliance/vendor-register",
  "/compliance/risk-register",
  "/outcomes",
  "/pipeline-analytics",
  "/accuracy",
  "/growth-intelligence",
  "/reply-intelligence",
  "/meeting-intelligence",
  "/database/contacts",
  "/database/companies",
  "/workflows/lead-gen-agency",
  "/proposals",
  "/chat-widgets",
  "/white-label",
  "/agents",
  "/workflows",
  "/privacy",
  "/terms",
  "/security",
  "/help",
  "/field-manual",
  "/mission-debrief",
  "/what-is-autonomous-ai-growth-platform",
]);

const DYNAMIC_ANALYTICS_PATHS: ReadonlyArray<{
  pattern: RegExp;
  template: string;
}> = [
  {
    pattern: /^\/intelligence\/company\/[^/]+\/?$/,
    template: "/intelligence/company/:id",
  },
  {
    pattern: /^\/sequence-optimizer\/[^/]+\/?$/,
    template: "/sequence-optimizer/:id",
  },
  { pattern: /^\/agents\/[^/]+\/?$/, template: "/agents/:id" },
  {
    pattern: /^\/workflows\/[^/]+\/editor\/?$/,
    template: "/workflows/:id/editor",
  },
  { pattern: /^\/portal\/[^/]+\/?$/, template: "/portal/:portalToken" },
  { pattern: /^\/book\/[^/]+\/?$/, template: "/book/:slug" },
  { pattern: /^\/invite\/[^/]+\/?$/, template: "/invite/:token" },
  { pattern: /^\/sign-in(?:\/.*)?$/, template: "/sign-in/:path" },
  { pattern: /^\/sign-up(?:\/.*)?$/, template: "/sign-up/:path" },
];

function sanitizePathname(pathname: string): string {
  const withLeadingSlash = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const normalized =
    withLeadingSlash.length > 1 && withLeadingSlash.endsWith("/")
      ? withLeadingSlash.slice(0, -1)
      : withLeadingSlash;

  if (STATIC_ANALYTICS_PATHS.has(normalized)) return normalized;

  const dynamicRoute = DYNAMIC_ANALYTICS_PATHS.find(({ pattern }) =>
    pattern.test(normalized),
  );
  return dynamicRoute?.template ?? "/:redacted";
}

export function sanitizeAnalyticsUrl(value: string): string {
  try {
    const url = new URL(value, window.location.origin);
    url.pathname = sanitizePathname(url.pathname);
    url.search = "";
    url.hash = "";
    return url.toString();
  } catch {
    return sanitizePathname(value.split(/[?#]/, 1)[0] ?? "");
  }
}

export function sanitizePostHogCapture(
  capture: CaptureResult | null,
): CaptureResult | null {
  if (!capture) return null;

  const properties = { ...capture.properties };
  for (const key of ["$current_url", "$referrer", "$initial_referrer"]) {
    if (typeof properties[key] === "string") {
      properties[key] = sanitizeAnalyticsUrl(properties[key]);
    }
  }
  if (typeof properties.$pathname === "string") {
    properties.$pathname = sanitizePathname(properties.$pathname);
  }

  // Page titles can contain customer or record names on authenticated pages.
  delete properties.$title;

  return {
    ...capture,
    properties,
    $set: undefined,
    $set_once: undefined,
  };
}

export function createPostHogAnalytics(
  client: PostHogClient,
  runtime: PostHogRuntimeConfig,
) {
  let initAttempted = false;
  let initialized = false;
  let currentUserId: string | null = null;
  let currentWorkspaceId: string | null = null;
  let authenticatedEventUserId: string | null = null;
  let activatedEventWorkspaceId: string | null = null;

  const init = (): boolean => {
    if (initialized) return true;
    if (initAttempted) return false;
    initAttempted = true;

    const key = runtime.key?.trim();
    const host = runtime.host?.trim();
    if (!runtime.enabled || !key || !host) return false;

    try {
      client.init(key, {
        api_host: host,
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        person_profiles: "identified_only",
        respect_dnt: true,
        disable_session_recording: true,
        disable_surveys: true,
        before_send: sanitizePostHogCapture,
      });
      initialized = true;
      return true;
    } catch {
      return false;
    }
  };

  const capture = (
    eventName: string,
    properties: Record<string, string | number | boolean> = {},
    options?: Pick<CaptureOptions, "uuid">,
  ): boolean => {
    if (
      !init() ||
      currentUserId === null ||
      currentWorkspaceId === null
    ) {
      return false;
    }
    try {
      const scopedProperties = {
        ...properties,
        lt_workspace_id: currentWorkspaceId,
      };
      return Boolean(
        options
          ? client.capture(eventName, scopedProperties, options)
          : client.capture(eventName, scopedProperties),
      );
    } catch {
      // Analytics failures must never affect the application lifecycle.
      return false;
    }
  };

  const reset = (): void => {
    if (!initialized) {
      currentUserId = null;
      currentWorkspaceId = null;
      authenticatedEventUserId = null;
      activatedEventWorkspaceId = null;
      return;
    }
    try {
      client.reset();
    } catch {
      // Analytics failures must never affect logout or account switching.
    } finally {
      currentUserId = null;
      currentWorkspaceId = null;
      authenticatedEventUserId = null;
      activatedEventWorkspaceId = null;
    }
  };

  const prepareUser = (userId: string): void => {
    const normalizedUserId = userId.trim();
    if (!normalizedUserId || !init()) return;
    if (currentUserId !== null && currentUserId !== normalizedUserId) {
      reset();
    }
  };

  const clearWorkspace = (): void => {
    if (!initialized) {
      currentWorkspaceId = null;
      activatedEventWorkspaceId = null;
      return;
    }
    try {
      client.resetGroups();
      client.unregister("lt_workspace_id");
    } catch {
      // Analytics failures must never affect workspace resolution.
    } finally {
      currentWorkspaceId = null;
      activatedEventWorkspaceId = null;
    }
  };

  const setWorkspace = (workspaceId: number | string): void => {
    const normalizedWorkspaceId = String(workspaceId).trim();
    if (!normalizedWorkspaceId || !init() || currentUserId === null) return;

    if (currentWorkspaceId !== normalizedWorkspaceId) {
      try {
        client.group("workspace", normalizedWorkspaceId);
        client.register({ lt_workspace_id: normalizedWorkspaceId });
        currentWorkspaceId = normalizedWorkspaceId;
      } catch {
        currentWorkspaceId = null;
        return;
      }
    }

    if (
      activatedEventWorkspaceId !== normalizedWorkspaceId &&
      capture(LT_POSTHOG_EVENTS.workspaceActivated)
    ) {
      activatedEventWorkspaceId = normalizedWorkspaceId;
    }
  };

  const syncUserContext = (
    userId: string,
    workspaceId: number | string | null,
  ): void => {
    if (workspaceId === null) {
      clearWorkspace();
      return;
    }

    const normalizedUserId = userId.trim();
    const normalizedWorkspaceId = String(workspaceId).trim();
    if (!normalizedUserId || !normalizedWorkspaceId || !init()) return;

    if (currentUserId !== null && currentUserId !== normalizedUserId) {
      reset();
    }

    const workspaceChanged = currentWorkspaceId !== normalizedWorkspaceId;
    if (workspaceChanged) {
      try {
        client.group("workspace", normalizedWorkspaceId);
        client.register({ lt_workspace_id: normalizedWorkspaceId });
        currentWorkspaceId = normalizedWorkspaceId;
      } catch {
        currentWorkspaceId = null;
        return;
      }
    }

    if (currentUserId !== normalizedUserId) {
      try {
        client.identify(normalizedUserId);
        currentUserId = normalizedUserId;
      } catch {
        currentUserId = null;
        return;
      }
    }

    if (
      activatedEventWorkspaceId !== normalizedWorkspaceId &&
      capture(LT_POSTHOG_EVENTS.workspaceActivated)
    ) {
      activatedEventWorkspaceId = normalizedWorkspaceId;
    }

    if (
      authenticatedEventUserId !== normalizedUserId &&
      capture(LT_POSTHOG_EVENTS.userAuthenticated)
    ) {
      authenticatedEventUserId = currentUserId;
    }
  };

  const capturePageview = (): void => {
    if (
      !initialized ||
      currentUserId === null ||
      currentWorkspaceId === null
    ) {
      return;
    }
    capture("$pageview");
  };

  const captureProductEvent = <EventName extends ProductEventName>(
    eventName: EventName,
    properties: ProductEventPropertiesMap[EventName],
  ): boolean => {
    const validated = validatedProductCapture(eventName, properties);
    if (!validated) return false;
    return capture(eventName, validated.properties, { uuid: validated.uuid });
  };

  return {
    init,
    prepareUser,
    reset,
    setWorkspace,
    clearWorkspace,
    syncUserContext,
    capturePageview,
    captureProductEvent,
    isInitialized: () => initialized,
  };
}

const browserClient: PostHogClient = {
  init: (apiKey, config) => {
    posthog.init(apiKey, config);
  },
  identify: (distinctId) => {
    posthog.identify(distinctId);
  },
  reset: () => {
    posthog.reset();
  },
  group: (groupType, groupKey) => {
    posthog.group(groupType, groupKey);
  },
  resetGroups: () => {
    posthog.resetGroups();
  },
  register: (properties) => {
    posthog.register(properties);
  },
  unregister: (property) => {
    posthog.unregister(property);
  },
  capture: (eventName, properties, options) => {
    return posthog.capture(eventName, properties, options);
  },
};

const analytics = createPostHogAnalytics(browserClient, {
  enabled: import.meta.env.VITE_POSTHOG_ENABLED === "true",
  key: import.meta.env.VITE_POSTHOG_KEY,
  host: import.meta.env.VITE_POSTHOG_HOST,
});

export const initPostHog = analytics.init;
export const preparePostHogUser = analytics.prepareUser;
export const resetPostHog = analytics.reset;
export const setPostHogWorkspace = analytics.setWorkspace;
export const clearPostHogWorkspace = analytics.clearWorkspace;
export const syncPostHogContext = analytics.syncUserContext;
export const capturePostHogPageview = analytics.capturePageview;
export const captureProductEvent = analytics.captureProductEvent;